import React, { useEffect } from 'react';
import './Terms.css';

const AppPrivacy: React.FC = () => {
  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
  <div className="terms-page">
    <div className="terms-content">
      <h1>HEAL PRIVACY POLICY</h1>
      <p><strong>Effective Date:</strong> October 25, 2026</p>
      <p>
        Heal Music Ltd. (&quot;Heal,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) values the privacy of individuals who use our websites,
        mobile applications, and other online products and services (collectively, the &quot;Services&quot;). This Privacy
        Policy explains how we collect, use, and disclose your personal information when you use our Services,
        interact with us, or contact customer support.
      </p>
      <p>
        By using our Services, you acknowledge that we will collect, use, and disclose your information as
        described in this Privacy Policy. If you connect Garmin, see{' '}
        <a href="#garmin">Garmin Connect Data</a>.
      </p>
      <p>
        If any specific service links to a different privacy notice, that notice will take precedence over this policy.
      </p>
      <p>
        This Privacy Policy includes additional disclosures for residents of California (CCPA), the European
        Economic Area (EEA), the UK, and Switzerland (GDPR) at the end.
      </p>

      <h2>1. Information We Collect</h2>
      <p>We may collect a variety of information from and about you or your devices, including:</p>

      <h3>A. Information You Provide to Us</h3>
      <ul>
        <li>
          <strong>Account Registration &amp; Profile Information</strong>
          <ul>
            <li>Name, email, phone number, username</li>
            <li>Social media profile (if linked)</li>
            <li>Profile photo (optional)</li>
            <li>Subscription preferences</li>
          </ul>
        </li>
        <li>
          <strong>Communications with Us</strong>
          <ul>
            <li>Customer support inquiries</li>
            <li>Feedback, survey responses, testimonials</li>
            <li>Messages exchanged with us through email, chat, or social media</li>
          </ul>
        </li>
        <li>
          <strong>Payment Information</strong> processed securely by third-party payment providers (e.g., Stripe, PayPal).
          Heal does not store full payment details.
        </li>
        <li>
          <strong>User-Generated Content</strong>
          <ul>
            <li>Music preferences, session feedback, listening habits, mood check-ins</li>
            <li>AI-generated music therapy data responses</li>
          </ul>
        </li>
        <li>
          <strong>Health &amp; Wellness Data (With Consent)</strong>
          <ul>
            <li>Sleep patterns, stress levels</li>
            <li>Heart rate variability (HRV)</li>
            <li>
              Other biometric data via connected wearables (e.g., Apple Watch, Apple Health, Oura Ring, Whoop, Fitbit,
              Garmin, AirPods Pro)
            </li>
          </ul>
        </li>
      </ul>

      <h3>B. Information We Collect Automatically</h3>
      <p>When you use our Services, we collect:</p>
      <ul>
        <li>
          <strong>Device Information</strong>
          <ul>
            <li>IP address, browser type, device model, OS version</li>
            <li>Mobile network provider</li>
          </ul>
        </li>
        <li>
          <strong>Usage Information</strong>
          <ul>
            <li>Sessions accessed, music played, listening history</li>
            <li>Interaction with AI-generated recommendations</li>
            <li>Features used, time spent on different sections</li>
          </ul>
        </li>
        <li>
          <strong>Cookies &amp; Tracking Technologies</strong> used for authentication, analytics, and personalization.
          Cookies may be set by third-party analytics/advertising providers.
        </li>
      </ul>

      <h3>C. Information from Third Parties</h3>
      <p>We may receive information from:</p>
      <ul>
        <li>
          <strong>Wearables &amp; Health Apps (With Permission)</strong>
          <ul>
            <li>Apple HealthKit, Google Health Connect, Fitbit, Oura, Whoop, Garmin, etc.</li>
          </ul>
        </li>
        <li>
          <strong>Social Media Platforms</strong> if you link Heal to your social media account
        </li>
        <li>
          <strong>Third-Party Analytics &amp; Payment Processors</strong>
          <ul>
            <li>Services like Google Analytics, Stripe, and Firebase</li>
          </ul>
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>
          <strong>Provide &amp; Maintain the Services</strong>
          <ul>
            <li>
              Deliver personalized music therapy and breathwork based on biometric driven insights, calendar, location,
              and time of day
            </li>
            <li>Track session engagement for improving recommendations</li>
            <li>Debug and improve app functionality</li>
          </ul>
        </li>
        <li>
          <strong>Process Transactions</strong>
          <ul>
            <li>Manage subscription payments and purchases</li>
            <li>Detect fraudulent activity</li>
          </ul>
        </li>
        <li>
          <strong>Personalize Your Experience</strong>
          <ul>
            <li>AI-generated recommendations based on HRV/heart rate response</li>
            <li>Adaptive recommendations based on physiological data</li>
          </ul>
        </li>
        <li>
          <strong>Communicate with You</strong>
          <ul>
            <li>Account notifications, customer support responses</li>
            <li>Promotional offers (with opt-out option)</li>
          </ul>
        </li>
        <li>
          <strong>Analyze &amp; Improve the Services</strong>
          <ul>
            <li>Monitor app performance, engagement trends</li>
            <li>Enhance AI-driven personalization</li>
          </ul>
        </li>
        <li>
          <strong>Comply with Legal Obligations</strong>
          <ul>
            <li>Tax filings, fraud prevention, regulatory compliance</li>
          </ul>
        </li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>
        We do not use health, biometric, or wearable data directly from wearables for advertising or marketing, and we
        never sell, license, or share this data with third parties. Garmin Connect Health API data is not sent to
        advertising or analytics tools (including Google Analytics and Meta/Facebook Pixel). How Garmin data is
        processed is described in <a href="#garmin">Garmin Connect Data</a>.
      </p>
      <p>
        We do not share personal data, however, we may share information in the following cases:
      </p>
      <ul>
        <li>
          <strong>With Service Providers</strong>
          <ul>
            <li>Payment processors, hosting providers, analytics services</li>
          </ul>
        </li>
        <li>
          <strong>With Legal &amp; Compliance Partners</strong>
          <ul>
            <li>If required by law (e.g., court orders, regulatory inquiries)</li>
          </ul>
        </li>
        <li>
          <strong>In Business Transfers</strong>
          <ul>
            <li>If Heal is acquired, merged, or involved in a sale of assets</li>
          </ul>
        </li>
        <li>
          <strong>With Your Consent</strong>
          <ul>
            <li>When you choose to share content from Heal publicly</li>
          </ul>
        </li>
      </ul>
      <p>We may also share aggregated or anonymized data that does not identify individuals.</p>

      <h3>3.1 How Wearable Connections Work</h3>
      <p>
        Heal connects to Garmin, Oura, Whoop, Apple Health, Polar, and other providers using OAuth 2.0. You authorise
        the connection on the provider&apos;s own consent screen — we never see or store your wearable password. You can
        disconnect any provider at any time from Heal&apos;s settings or the provider&apos;s dashboard; on disconnection,
        we revoke access tokens immediately and stop new data syncs. Garmin is described in more detail in{' '}
        <a href="#garmin">Garmin Connect Data</a>.
      </p>

      <h2 id="garmin">Garmin Connect Data</h2>
      <p>
        This section describes how Heal Music Ltd. collects, uses, processes, and stores data received from Garmin
        through the Garmin Connect Developer Program (Health API).
      </p>

      <h3>Collection</h3>
      <p>
        We receive Garmin data only if you choose to connect Garmin Connect in the Heal app. Connection uses OAuth 2.0
        on Garmin&apos;s consent screen. Heal never sees or stores your Garmin password. After you consent, Garmin
        delivers health summaries to HTTPS endpoints that Heal owns and operates. Garmin does not deliver that data to
        any third-party endpoint.
      </p>
      <p>Depending on the permissions you grant in Garmin Connect, this may include:</p>
      <ul>
        <li>Overnight and daily heart-rate variability (HRV)</li>
        <li>Resting heart rate</li>
        <li>Sleep duration, timing, and sleep stages</li>
        <li>Daily wellness summaries (for example pulse ox, when you share them)</li>
        <li>A Garmin-issued user identifier used only to keep your Heal account linked to Garmin</li>
      </ul>
      <p>We do not request Garmin Training, Courses, or Activity APIs.</p>

      <h3>Use</h3>
      <p>We use Garmin data only to operate Heal for the connected user:</p>
      <ul>
        <li>Show your own recovery, sleep, HRV, and resting heart rate in Insights</li>
        <li>Personalise music and breathwork sessions</li>
        <li>Maintain the Garmin connection (token refresh and permission updates)</li>
      </ul>
      <p>
        We do not use Garmin data for advertising, marketing, sale, or licensing, and we do not combine it with
        advertising identifiers.
      </p>

      <h3>Storage and processing</h3>
      <p>
        Garmin OAuth tokens and health summaries are stored on Heal&apos;s servers. Tokens stay on the server; they are
        not stored in the iOS app and are not shared with any external party. Health summaries are stored against your
        Heal account and labelled as coming from Garmin.
      </p>

      <h3>Third parties and AI</h3>
      <p>
        Garmin API credentials are issued to Heal Music Ltd. and are not shared, transferred, or used by any other
        organisation. Garmin delivers API data only to Heal-owned endpoints.
      </p>
      <p>
        We use infrastructure processors acting on our instructions to host the Heal backend and database (currently
        Railway). They store data on our behalf; they are not independent recipients of Garmin data.
      </p>
      <p>
        Heal uses a third-party AI service to generate personalised playlists. That service may receive Heal-computed
        aggregates for your account (for example average overnight HRV or sleep duration). Those aggregates can be
        derived from Garmin when Garmin is the source for that day. We do not send Garmin API payloads, raw
        sample-level Garmin data, Garmin user identifiers, or OAuth tokens to any AI provider or other third party.
      </p>
      <p>
        We do not send Garmin data to analytics or advertising tools, including Google Analytics and Meta/Facebook Pixel.
      </p>

      <h3>Retention, disconnection, and deletion</h3>
      <p>
        We keep Garmin data while your Garmin account stays connected and your Heal account is active, or longer if the
        law requires it. You can disconnect Garmin at any time in Heal settings. Disconnecting:
      </p>
      <ul>
        <li>Calls Garmin&apos;s user de-registration endpoint</li>
        <li>Deletes our stored Garmin tokens immediately</li>
        <li>Stops new Garmin syncs</li>
      </ul>
      <p>
        You can also revoke Heal from Garmin Connect. Garmin then notifies us, and we delete the tokens. To request
        deletion of stored Garmin health summaries, email{' '}
        <a href="mailto:privacy@healmusicapp.com">privacy@healmusicapp.com</a>.
      </p>

      <h2>4. Data Retention &amp; Security</h2>
      <ul>
        <li>
          <strong>Retention Period</strong>
          <ul>
            <li>We keep your data only as long as necessary for providing the Services</li>
            <li>If legally required, we may retain some data for tax or compliance reasons</li>
          </ul>
        </li>
        <li>
          <strong>Security Measures</strong>
          <ul>
            <li>Encryption, secure storage, multi-factor authentication</li>
            <li>Access restrictions to prevent unauthorized access</li>
          </ul>
        </li>
        <li>
          <strong>User Responsibility</strong>
          <ul>
            <li>Use strong passwords, enable two-factor authentication</li>
          </ul>
        </li>
      </ul>

      <h2>5. Advertising &amp; Cookies</h2>
      <p>We use cookies and tracking technologies for:</p>
      <ul>
        <li>Authentication &amp; Security</li>
        <li>Analytics &amp; Performance Monitoring</li>
        <li>Personalized Advertising (where allowed)</li>
      </ul>
      <p><strong>Third-Party Advertising &amp; Analytics</strong></p>
      <p>Some third-party services we use:</p>
      <ul>
        <li>Google Analytics (user behavior tracking)</li>
        <li>Facebook Pixel (advertising insights)</li>
        <li>Apple &amp; Google Advertising IDs</li>
      </ul>
      <p><strong>Opt-Out Options:</strong></p>
      <ul>
        <li>
          <a href="https://youradchoices.com/" target="_blank" rel="noopener noreferrer">YourAdChoices</a> (U.S.)
        </li>
        <li>
          <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer">Your Online Choices</a> (EU)
        </li>
      </ul>

      <h2>6. International Data Transfers</h2>
      <ul>
        <li>We process data in multiple countries, including the UK, US, and EU</li>
        <li>We use Standard Contractual Clauses (SCCs) to protect cross-border data transfers</li>
      </ul>

      <h2>7. Your Privacy Rights</h2>
      <p>Depending on your location, you may have the right to:</p>
      <ul>
        <li>Access &amp; Request a Copy of Your Data</li>
        <li>Correct Inaccurate Information</li>
        <li>Request Data Deletion (subject to legal obligations)</li>
        <li>Opt-Out of Marketing Communications</li>
        <li>Restrict Processing of Certain Data</li>
      </ul>
      <p><strong>How to Exercise Your Rights:</strong></p>
      <ul>
        <li>Email: <a href="mailto:privacy@healmusicapp.com">privacy@healmusicapp.com</a></li>
        <li>Manage preferences via app settings</li>
      </ul>

      <h2>8. Additional Information for California, EEA, UK, and Switzerland</h2>
      <h3>California Residents (CCPA Rights)</h3>
      <p>You may request:</p>
      <ul>
        <li>A summary of data collected</li>
        <li>Deletion of personal information</li>
        <li>To opt out of &quot;sharing&quot; for targeted advertising</li>
      </ul>
      <p>
        Heal does not sell personal data but may engage in limited data-sharing for analytics.
      </p>
      <h3>EEA, UK, and Switzerland (GDPR Rights)</h3>
      <ul>
        <li>Right to Object to Processing (e.g., marketing)</li>
        <li>Right to Withdraw Consent (for optional data collection)</li>
        <li>Right to Data Portability</li>
        <li>Right to File a Complaint with a data protection authority</li>
      </ul>
      <p>To exercise GDPR rights, contact <a href="mailto:privacy@healmusicapp.com">privacy@healmusicapp.com</a>.</p>

      <h2>9. Children&apos;s Privacy</h2>
      <p>
        Heal is not intended for children under 16. If we learn that we have collected information from a child under
        16, we will delete it.
      </p>

      <h2>10. Changes to This Privacy Policy</h2>
      <p>We may update this policy periodically. Any material changes will be:</p>
      <ul>
        <li>Posted on our website</li>
        <li>Notified via email (if applicable)</li>
      </ul>

      <h2>11. Contact Us</h2>
      <p>For privacy-related inquiries:</p>
      <ul>
        <li>Email: <a href="mailto:privacy@healmusicapp.com">privacy@healmusicapp.com</a></li>
        <li>Support: <a href="mailto:help@healmusicapp.com">help@healmusicapp.com</a></li>
      </ul>
      <p>For EU data protection inquiries:</p>
      <ul>
        <li>Email: <a href="mailto:privacy@healmusicapp.com">privacy@healmusicapp.com</a></li>
      </ul>
    </div>
  </div>
  );
};

export default AppPrivacy;
